import { InputTypes, UI, HStack, Stack, VStack } from '@genial/components';
import { BoxBorder, Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, Button, TextArea } from 'native-base';
import React, { useEffect, useState } from 'react';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { fundsWithdrawDateQuotation } from '../../../../../../store/redemptionSolicitation/selector';
import { FundWithdrawAssetNormalized } from '../../../../../../store/redemptionSolicitation/types';
import { CardTitle } from '../../../../components/CardTitle';
import { CustomItem } from '../../../../components/CustomItem';
import { HighlightedInfos } from '../../../../components/HighlightedInfos';
import { InfoItem } from '../../../../components/InfoItem';
import { ProductCard } from '../../../../components/ProductCard';

import { defaultDateError, limitTimeExceededError } from './constants';

export interface ConfirmationStepInfos {
  selectedDate: Date;
  channel: string;
  observation?: string;
  quotationDateFormatted: string;
  liquidityDateFormatted: string;
}

export interface ConfirmationStepProps {
  isExecuting: boolean;
  accountId: number;
  totalValueOption: boolean;
  valuesSelected: { grossValue: number; liquidityValue: number };
  fundAssetNormalized: FundWithdrawAssetNormalized;
  showLimitTimeExceeded: boolean;
  setShowLimitTimeExceeded: (value: boolean) => void;
  handleConfirm: (infos: ConfirmationStepInfos) => void;
  handleCancel: () => void;
}

export const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  isExecuting,
  accountId,
  totalValueOption,
  valuesSelected,
  fundAssetNormalized,
  showLimitTimeExceeded,
  setShowLimitTimeExceeded,
  handleCancel,
  handleConfirm,
}) => {
  const [selectedOption, setSelectedOption] = useState('phone');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [textAreaInput, setTextAreaInput] = useState('');
  const [dateIsValid, setDateIsValid] = useState(true);

  const fundInfo = fundAssetNormalized.fund;

  const { data, loading, error } = useRecoilQuery(
    fundsWithdrawDateQuotation({
      date: selectedDate,
      totalValueOption,
      productId: fundInfo.fundId,
      accountId,
    })
  );

  const handlePressConfirm = () => {
    if (!dateIsValid) {
      return;
    }
    handleConfirm({
      selectedDate,
      channel: selectedOption,
      observation: textAreaInput,
      liquidityDateFormatted: data?.liquidityDateFormatted || '--',
      quotationDateFormatted: data?.quotationDateFormatted || '--',
    });
  };

  const getErrorMessage = () => {
    if (showLimitTimeExceeded) {
      return limitTimeExceededError;
    }
    return defaultDateError;
  };

  useEffect(() => {
    setShowLimitTimeExceeded(false);
    setDateIsValid(!Utils.Date.IsPast(selectedDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <ProductCard barColor="product.fundos">
        <VStack space={Spacing['stack.xxxs']}>
          <VStack space={Spacing['stack.quarck']}>
            <CustomItem label="Você está solicitando o resgate para:" />
            <CardTitle title={fundInfo.fundNameNormalized} />
          </VStack>
          <HighlightedInfos
            infos={[
              { label: 'Resgate Pendente', value: fundInfo.pendingRedemptionValueNormalized },
              { label: 'Total Bruto', value: fundInfo.grossTotalValueNormalized },
              { label: 'Valor Bruto Resgate', value: Utils.Mask.Money(valuesSelected.grossValue) },
              ...(valuesSelected.liquidityValue
                ? [
                    {
                      label: 'Valor Líquido Resgate',
                      value: Utils.Mask.Money(valuesSelected.liquidityValue),
                    },
                  ]
                : []),
            ]}
          />
          <UI.Text variant="semibold" color="neutral.black">
            Data do Resgate
          </UI.Text>

          <VStack space={Spacing['stack.quarck']} w="full" zIndex={5}>
            <UI.Text color="neutral.darkest">Escolha a data para resgate:</UI.Text>

            <UI.DatePicker
              placeholder={Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy')}
              onClear={() => setSelectedDate(new Date())}
              onSelectDate={setSelectedDate}
              date={selectedDate}
              availableRange={{ past: new Date() }}
              inputComponentProps={
                showLimitTimeExceeded || error
                  ? ({ error: { message: getErrorMessage() } } as InputTypes.Props)
                  : undefined
              }
            />
          </VStack>

          <InfoItem
            label="Data de Cotização"
            value={data?.quotationDateFormatted}
            loading={loading}
          />
          <InfoItem
            label="Data de Liquidação"
            value={data?.liquidityDateFormatted}
            loading={loading}
          />

          <UI.Text variant="semibold" color="neutral.black">
            Cliente confirmou o resgate
          </UI.Text>

          <VStack space={Spacing['stack.quarck']} w="full">
            <UI.Text color="neutral.darkest">Selecione o canal</UI.Text>
            <Stack bgColor={Color.background} borderRadius={BoxBorder.thick}>
              <UI.Select
                placeholder="Selecione o canal"
                options={[
                  { label: 'Via Telefone', value: 'phone' },
                  { label: 'Via E-mail', value: 'email' },
                  { label: 'WhatsApp', value: 'whatsApp' },
                ]}
                selectedValue={selectedOption}
                onValueChange={setSelectedOption}
              />
            </Stack>
          </VStack>

          <VStack space={Spacing['stack.quarck']} w="full">
            <UI.Text color="neutral.darkest">Observação</UI.Text>
            <Box height={137}>
              <TextArea
                flex={1}
                borderRadius={Spacing['inline.nano']}
                backgroundColor={Color.background}
                borderColor={'transparent'}
                _focus={{
                  backgroundColor: 'transparent',
                  borderColor: Color['brand.primary.principal'],
                  borderWidth: 1,
                }}
                _hover={{ borderColor: Color['brand.primary.principal'] }}
                value={textAreaInput}
                placeholder="Existe alguma informação sobre essa autorização"
                autoCompleteType={'off'}
                onChangeText={setTextAreaInput}
              />
            </Box>
          </VStack>
        </VStack>
      </ProductCard>
      <HStack
        alignSelf="flex-end"
        space={Spacing['inline.xxxs']}
        w={Utils.Responsive.DesktopPhone({ phone: 'full', desktop: 'auto' })}
        justifyContent={Utils.Responsive.DesktopPhone({
          phone: 'space-between',
          desktop: 'inherit',
        })}>
        <UI.Button
          variant="secondary"
          onPress={handleCancel}
          w={Utils.Responsive.DesktopPhone({ phone: '45%', desktop: 'auto' })}>
          Cancelar
        </UI.Button>
        <Button
          variant="callToAction"
          onPress={handlePressConfirm}
          isDisabled={!dateIsValid || loading || !!error || showLimitTimeExceeded}
          isLoadingText="Confirmando..."
          spinnerPlacement="end"
          isLoading={isExecuting}
          w={Utils.Responsive.DesktopPhone({ phone: '45%', desktop: 'auto' })}>
          Confirmar
        </Button>
      </HStack>
    </VStack>
  );
};
